// PlaySandbox — the play-test surface (spec §6.1 #5 / P1a acceptance). A 4-seat local game driven by
// the engine reducer: you act for EVERY seat from its legalMoves, rig-a-deal with a fixed seed,
// step/auto, reveal-all, and see the score breakdown on a win. Save→reflect: when a rule changes
// (revision bump), the open game re-folds its scripted intents against the new RuleSet without a
// reload (SandboxGame.rebuild) — the sandbox state is preserved, which is the whole point.
import { useEffect, useRef, useState } from 'react';
import type { Intent, Seat } from '@mj/protocol';
import type { GameEvent } from '@mj/engine';
import { useStudioStore, useStudioApi } from '../useStore';
import { makeResultName } from '../yakuName';
import { TileRow } from '../render/TileView';
import { SandboxGame, autoChoose, seedFromNumber, type ActableSeat } from '../sandbox';

function intentLabel(i: Intent): string {
  switch (i.kind) {
    case 'discard':
      return `discard ${i.tile}`;
    case 'chii':
      return `chii ${i.tiles.join('+')}`;
    case 'ankan':
      return `ankan ${i.tile}`;
    case 'shouminkan':
      return `kakan ${i.tile}`;
    default:
      return i.kind;
  }
}

function terminalSummary(events: GameEvent[]): GameEvent | null {
  const last = events[events.length - 1];
  if (last && (last.event === 'win' || last.event === 'ryuukyoku')) return last;
  return null;
}

export function PlaySandbox() {
  const api = useStudioApi();
  const revision = useStudioStore((s) => s.revision);
  const catalog = useStudioStore((s) => s.catalog);
  const resultName = makeResultName(catalog);
  const [seedN, setSeedN] = useState(101);
  const [revealAll, setRevealAll] = useState(true);
  const [, forceRender] = useState(0);
  const gameRef = useRef<SandboxGame | null>(null);

  // (Re)create the game when the seed changes. The RuleSet + WORKING catalog at creation are the
  // current ones, so a custom (DSL) yaku in the catalog fires when a sandbox hand wins.
  useEffect(() => {
    const st = api.getState();
    gameRef.current = new SandboxGame(st.ruleSet, seedFromNumber(seedN), st.catalog);
    forceRender((n) => n + 1);
  }, [api, seedN]);

  // Save→reflect: when the RuleSet/catalog changes (revision), re-fold the scripted moves against it.
  useEffect(() => {
    const g = gameRef.current;
    if (!g) return;
    const st = api.getState();
    g.rebuild(st.ruleSet, st.catalog);
    forceRender((n) => n + 1);
  }, [api, revision]);

  const game = gameRef.current;
  // Recompute the actable seats on every render so the action buttons always reflect live state.
  const liveActable: ActableSeat[] = game ? game.actableSeats() : [];
  const terminal = game ? terminalSummary(game.eventLog) : null;

  const act = (seat: Seat, intent: Intent) => {
    if (!game) return;
    game.apply(seat, intent);
    forceRender((n) => n + 1);
  };

  const step = () => {
    if (!game) return;
    if (game.needsExhaustiveDraw()) {
      game.finalizeExhaustive();
      forceRender((n) => n + 1);
      return;
    }
    const seats = game.actableSeats();
    if (seats.length === 0) return;
    const winner = seats.find((a) => a.moves.some((m) => m.kind === 'ron' || m.kind === 'tsumo'));
    const target = winner ?? seats[0]!;
    game.apply(target.seat, autoChoose(target.moves));
    forceRender((n) => n + 1);
  };

  const auto = () => {
    if (!game) return;
    for (let i = 0; i < 400; i++) {
      if (game.isTerminal()) break;
      if (game.needsExhaustiveDraw()) {
        game.finalizeExhaustive();
        break;
      }
      const seats = game.actableSeats();
      if (seats.length === 0) break;
      const winner = seats.find((a) => a.moves.some((m) => m.kind === 'ron' || m.kind === 'tsumo'));
      const target = winner ?? seats[0]!;
      const r = game.apply(target.seat, autoChoose(target.moves));
      if (!r.ok) break;
    }
    forceRender((n) => n + 1);
  };

  const reset = () => {
    if (!game) return;
    const st = api.getState();
    game.reset(st.ruleSet, st.catalog);
    forceRender((n) => n + 1);
  };

  if (!game) return <div>loading…</div>;
  const s = game.state;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13 }}>
          seed{' '}
          <input type="number" value={seedN} onChange={(e) => setSeedN(Number(e.target.value))} style={{ width: 90 }} />
        </label>
        <button onClick={step} disabled={game.isTerminal()}>step</button>
        <button onClick={auto} disabled={game.isTerminal()}>auto</button>
        <button onClick={reset}>reset (same seed)</button>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={revealAll} onChange={(e) => setRevealAll(e.target.checked)} /> reveal-all
        </label>
        <span style={{ fontSize: 12, color: '#666' }}>
          phase: <strong>{s.phase}</strong> · turn: {s.turn} · wall: {s.wall.length} · moves: {game.moveLog.length}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {s.players.map((p) => {
          const isTurn = s.turn === p.seat && (s.phase === 'draw' || s.phase === 'discard');
          const seatActable = liveActable.find((a) => a.seat === p.seat);
          return (
            <div
              key={p.seat}
              style={{
                border: isTurn ? '2px solid #2b6e2b' : '1px solid #ddd',
                borderRadius: 6,
                padding: 8,
                background: '#fff',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <strong>
                  seat {p.seat} {p.seat === s.dealer ? '(親)' : ''} {p.seatWind ?? ''}
                </strong>
                <span>score {p.score}{p.riichi?.declared ? ' · 立直' : ''}</span>
              </div>
              {revealAll || p.seat === 0 ? (
                <TileRow tiles={p.hand} size={24} />
              ) : (
                <span style={{ fontSize: 12, color: '#999' }}>{p.hand.length} tiles (hidden)</span>
              )}
              {p.melds.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {p.melds.map((m, i) => (
                    <span key={i} style={{ marginRight: 6 }}>
                      <TileRow tiles={m.tiles} size={20} />
                    </span>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 4, fontSize: 11, color: '#888' }}>
                discards: {p.discards.map((d) => d.tile).join(' ') || '—'}
              </div>
              {seatActable && !game.isTerminal() && (
                <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {seatActable.moves.slice(0, 24).map((m, i) => (
                    <button
                      key={i}
                      onClick={() => act(p.seat, m)}
                      style={{
                        fontSize: 11,
                        padding: '2px 6px',
                        border: m.kind === 'tsumo' || m.kind === 'ron' ? '1px solid #2b6e2b' : '1px solid #ccc',
                        color: m.kind === 'tsumo' || m.kind === 'ron' ? '#2b6e2b' : '#333',
                      }}
                    >
                      {intentLabel(m)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {game.needsExhaustiveDraw() && !terminal && (
        <div style={{ marginTop: 10, padding: 8, background: '#fff7e6', borderRadius: 6 }}>
          Wall exhausted —{' '}
          <button onClick={step}>finalize ryuukyoku</button>
        </div>
      )}

      {terminal && (
        <div style={{ marginTop: 12, padding: 12, border: '1px solid #2b6e2b', borderRadius: 6, background: '#f3faf3' }}>
          <strong>
            {terminal.event === 'win' ? '和了' : `流局 (${terminal.event === 'ryuukyoku' ? terminal.kind : ''})`}
          </strong>
          {terminal.event === 'win' && (
            <div style={{ marginTop: 6, fontSize: 13 }}>
              {terminal.winners.map((w, wi) => {
                const res = terminal.results[wi]!;
                return (
                  <div key={w} style={{ marginBottom: 6 }}>
                    seat {w}:{' '}
                    {res.yakuman > 0 ? `役満×${res.yakuman}` : `${res.han}han ${res.fu}fu`} ={' '}
                    <strong>{res.points.toLocaleString()}</strong>
                    <div style={{ fontSize: 11, color: '#555' }}>
                      yaku: {res.yaku.map((y) => `${resultName(y.id, y.han)}(${y.han})`).join(', ') || '—'} · payments [{res.payments.join(', ')}]
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {terminal.event === 'ryuukyoku' && (
            <div style={{ marginTop: 6, fontSize: 13 }}>
              tenpai: {terminal.tenpai.join(', ') || '—'} · payments [{terminal.payments.join(', ')}]
            </div>
          )}
        </div>
      )}
    </div>
  );
}
